import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// Existing project modules — unchanged
import { auth } from "../authV2";
import {IconSidebarV2} from "./../IconSidebar_ChatHistory/IconSidebarV2";

import "./HomeV2.css";
import "./PublicPageShared.css";

// Design system primitives (Phase 2)
import {
  Button,
  Input,
  Spinner,
  ThemeToggle,
  toast,
  cn,
  SunIcon, // not used here directly, kept for reference
} from "../../../styles/facemeet-ui";

import {
  Brand,
  ThemeSlot,
  Ambient,
  isValidEmail,
} from "./PublicHelpers";



const CAPACITY_OPTIONS = [
  { label: "5 — Small team", value: 5 },
  { label: "10 — Workshop", value: 10 },
  { label: "25 — Team call", value: 25 },
  { label: "50 — Town hall", value: 50 },
  { label: "100 — Webinar", value: 100 },
  { label: "250 — Conference", value: 250 },
  { label: "500 — Large event", value: 500 },
  { label: "1,000 — Broadcast", value: 1000 },
  { label: "5,000 — Mass event", value: 5000 },
  { label: "Custom…", value: "custom" },
];

// Plan caps are READ FROM THE SERVER via GET /v2/plans on every page
// load — see the useEffect inside HomePageV2. The catalog below is a
// FALLBACK only, used during the brief window between mount and the
// first successful fetch. If the SuperAdmin edits Basic to 50, the
// homepage will show 50 within milliseconds of the page loading; the
// numbers here just keep the form usable if the plans endpoint is
// down.
const FALLBACK_PLAN_CAPS = {
  free:  5,
  basic: 20,
  pro:   100,
};
const fallbackCap = (planCode) =>
  FALLBACK_PLAN_CAPS[planCode] ?? FALLBACK_PLAN_CAPS.free;

export function HomePageV2() {
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_SERVER_URL;

  // Session state
  const [session, setSession] = useState(() => auth.getSession());
  useEffect(() => {
    auth.validate().then((s) => setSession(s));
    return auth.subscribe(setSession);
  }, []);

  // Live plan catalog. Replaces hardcoded caps so SuperAdmin edits to
  // basic/pro/etc. take effect on the homepage without a redeploy.
  // Best-effort: on fetch failure we fall back to FALLBACK_PLAN_CAPS
  // and the form stays usable.
  const [allPlans, setAllPlans] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/v2/plans`);
        if (!res.ok) return;
        const data = await res.json();
        const items = Array.isArray(data) ? data : (data.items || []);
        if (!cancelled) setAllPlans(items);
      } catch {
        /* keep fallback caps */
      }
    })();
    return () => { cancelled = true; };
  }, [API_URL]);

  // Create form
  const [hostEmail, setHostEmail] = useState(() => localStorage.getItem("userEmail") || "");
  const [hostUsername, setHostUsername] = useState(() => localStorage.getItem("username") || "");
  const [meetingAgenda, setMeetingAgenda] = useState("");
  const [meetingType, setMeetingType] = useState("public");
  const [fdaId, setFdaId] = useState(() => localStorage.getItem("fdaId") || "");
  const [selectedCap, setSelectedCap] = useState(5);
  const [customCap, setCustomCap] = useState("");
  const [isCustom, setIsCustom] = useState(false);

  // Plan-aware cap + entitlements, derived from the LIVE plan catalog
  // when available, falling back to the static map during the first
  // render or if /v2/plans is unreachable.
  const planCode = session?.currentPlan || "free";
  const livePlan = allPlans.find((p) => p.planCode === planCode);
  const planCap = livePlan?.features?.maxParticipants ?? fallbackCap(planCode);
  // Private meetings are paid-only. Backend re-checks this on
  // create-room — frontend disabling is purely for UX clarity.
  // Prefer the live `features.privateMeetings` flag if present; else
  // fall back to the conservative "paid only" rule.
  const canCreatePrivate =
    livePlan?.features?.privateMeetings !== undefined
      ? !!livePlan.features.privateMeetings
      : (planCode === "basic" || planCode === "pro");

  // Plan expiry display: short form in the chip, full date in the
  // hover title. /auth/me returns planExpiresAt as ISO-with-Z so
  // new Date() parses to local time correctly.
  let planExpiryShort = null;
  let planExpiryTitle = planCode === "free" ? "Free tier" : "Active plan";
  if (planCode !== "free" && session?.planExpiresAt) {
    const d = new Date(session.planExpiresAt);
    if (!Number.isNaN(d.getTime())) {
      const now = new Date();
      const days = Math.ceil((d - now) / 86400000);
      if (days < 0) {
        planExpiryShort = "· expired";
        planExpiryTitle = `Expired on ${d.toLocaleDateString()}`;
      } else {
        planExpiryShort = `· renews ${d.toLocaleDateString()}`;
        planExpiryTitle = `Renews on ${d.toLocaleString()}`;
      }
    }
  }
  // Build the dropdown options. We keep the standard sizes that fit
  // within the plan, plus we INJECT the exact planCap as its own
  // option if it isn't already a standard size — so a plan configured
  // for 20 actually offers "20" in the picker, not just "10" (the
  // next-lower standard). This is the fix for the "Basic configured
  // for 20 only unlocks 10" report — the previous filter quietly
  // dropped 25 → 5,10 was the most a Basic user could pick.
  const visibleCapacityOptions = (() => {
    const base = CAPACITY_OPTIONS.filter(
      (opt) => opt.value === "custom" || (typeof opt.value === "number" && opt.value <= planCap),
    );
    const hasExactCap = base.some((opt) => opt.value === planCap);
    if (!hasExactCap) {
      // Insert just before the "Custom…" sentinel so the order is
      // preserved (smallest → largest standard → exact-cap → custom).
      const customIdx = base.findIndex((opt) => opt.value === "custom");
      const exactOpt = { label: `${planCap} — Your plan limit`, value: planCap };
      if (customIdx === -1) {
        base.push(exactOpt);
      } else {
        base.splice(customIdx, 0, exactOpt);
      }
    }
    return base;
  })();
  // If the user upgrades/downgrades while on this page, snap the
  // selected value back into range so the form never carries an invalid
  // number into create-room.
  useEffect(() => {
    if (isCustom) return;
    if (typeof selectedCap === "number" && selectedCap > planCap) {
      setSelectedCap(planCap);
    }
  }, [planCap, isCustom, selectedCap]);
  // Same idea for meeting type: a downgrade to Free must not leave a
  // disabled "private" selected in state.
  useEffect(() => {
    if (!canCreatePrivate && meetingType === "private") {
      setMeetingType("public");
      setFdaId("");
    }
  }, [canCreatePrivate, meetingType]);
  const [isScheduled, setIsScheduled] = useState(false);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [meetingLink, setMeetingLink] = useState("");
  const [linkCopied, setLinkCopied] = useState(false);

  // Join form
  const [roomId, setRoomId] = useState("");
  const [joinError, setJoinError] = useState("");

  // Capacity helpers
  const handleCapacityChange = (e) => {
    const val = e.target.value;
    if (val === "custom") { setIsCustom(true); setSelectedCap("custom"); }
    else { setIsCustom(false); setSelectedCap(Number(val)); }
  };
  const getMaxParticipants = () => {
    let n;
    if (isCustom) {
      n = parseInt(customCap, 10);
      if (isNaN(n) || n < 1) n = planCap;
    } else {
      n = selectedCap === "custom" ? planCap : selectedCap;
    }
    // Hard ceiling at the plan cap — defense in depth. The backend
    // also caps this, but we don't want to submit a number we know
    // will be silently clamped.
    return Math.min(n, planCap);
  };

  const handleCreateRoom = async () => {
    setCreateError("");
    setMeetingLink("");

    if (!session?.token) {
      setCreateError("Please sign in to create a meeting.");
      navigate("/v2/login", { replace: false });
      return;
    }
    if (!hostEmail.trim()) return setCreateError("Your email is required");
    if (!isValidEmail(hostEmail)) return setCreateError("Enter a valid email address");
    if (!hostUsername.trim()) return setCreateError("Enter your display name");
    if (!meetingAgenda.trim()) return setCreateError("Meeting agenda is required");
    if (meetingType === "private" && !fdaId.trim())
      return setCreateError("User ID is required for private meetings");
    if (isScheduled) {
      if (!startTime || !endTime)
        return setCreateError("Start and end time are required for scheduled meetings");
      if (new Date(startTime) >= new Date(endTime))
        return setCreateError("End time must be after start time");
      if (new Date(startTime) < new Date())
        return setCreateError("Start time must be in the future");
    }

    try {
      setIsCreating(true);
      const res = await auth.fetch(`${API_URL}/v2/create-room`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          hostEmail: hostEmail.trim().toLowerCase(),
          hostUsername: hostUsername.trim(),
          maxParticipants: getMaxParticipants(),
          meeting_type: meetingType,
          meetingAgenda: meetingAgenda.trim(),
          fdaId: meetingType === "private" ? fdaId.trim() : null,
          isScheduled,
          startTime: isScheduled ? startTime : null,
          endTime: isScheduled ? endTime : null,
        }),
      });

      if (res.status === 401) {
        setCreateError("Your session ended. Please sign in again.");
        navigate("/v2/login", { replace: true });
        return;
      }
      const data = await res.json();
      if (!res.ok) { setCreateError(data.detail || "Failed to create room"); return; }

      const link = `${window.location.origin}/v2/join/${data.roomId}${
        isScheduled ? `?start=${startTime}&end=${endTime}` : ""
      }`;
      const normalEmail = hostEmail.trim().toLowerCase();
      localStorage.setItem("userEmail", normalEmail);
      localStorage.setItem("username", hostUsername.trim());

      if (isScheduled) {
        setMeetingLink(link);
        toast.success("Meeting scheduled — share the link below");
      } else {
        navigate(`/v2/room/${data.roomId}`, {
          state: {
            email: normalEmail,
            username: hostUsername.trim(),
            fdaId: meetingType === "private" ? fdaId.trim() : null,
          },
        });
      }
    } catch {
      setCreateError("Network error — please check your connection");
    } finally {
      setIsCreating(false);
    }
  };

  const handleCopyLink = () => {
    if (!meetingLink) return;
    navigator.clipboard.writeText(meetingLink).then(() => {
      setLinkCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setLinkCopied(false), 2500);
    });
  };

  const handleJoinRoom = (e) => {
    e.preventDefault();
    setJoinError("");
    if (!roomId.trim()) return setJoinError("Enter the Room ID or paste an invite link");
    const id = roomId.trim().split("/").pop();
    navigate(`/v2/join/${id}`);
  };

  const handleSignOut = () => {
    toast.info("Signed out");
    auth.logout(navigate);
  };

  return (
    <div className="fm-page">
      <Ambient />
      <ThemeSlot />

      <div className="fm-page__inner">
        {/* Session pill */}
        <div className="fm-pill" role="status">
          {session ? (
            <>
              <span className="fm-pill__dot" />
              <span>Signed in as <b>{session.username || session.fdaId}</b></span>

              {/* Plan chip — reads currentPlan from /auth/me. Always
                  visible (even on Free) so users have an unambiguous
                  indicator of what they're on, plus a one-click route
                  to the pricing page. */}
              <span className={`fm-plan-chip fm-plan-chip--${planCode}`} title={planExpiryTitle}>
                <span className="fm-plan-chip__dot" />
                {planCode === "free" ? "Free plan"
                  : planCode === "basic" ? "Basic plan"
                  : planCode === "pro" ? "Pro plan"
                  : `${planCode} plan`}
                {planExpiryShort && <span className="fm-plan-chip__expiry">{planExpiryShort}</span>}
              </span>

              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/v2/pricing")}
              >
                {planCode === "free" ? "Upgrade" : "View plans"}
              </Button>

              <Button variant="ghost" size="sm" onClick={handleSignOut}>Sign out</Button>
            </>
          ) : (
            <>
              <span style={{ color: "var(--text-faint)" }}>Not signed in — join works without an account.</span>
              <Button variant="primary" size="sm" onClick={() => navigate("/v2/login")}>Sign in</Button>
            </>
          )}
        </div>

        <Brand tagline="Connect with anyone, anywhere." />

        <div className="fm-panels">
          {/* CREATE PANEL */}
          <section
            className={cn("fm-panel", "fm-panel--accent", !session && "fm-panel--locked")}
            // data-disabled={!session}
            aria-labelledby="home-create-title"
          >
            <div className="fm-panel__head">
              <span className="fm-panel__eyebrow">{session ? "✨ Host" : "🔒 Locked"}</span>
              <h2 id="home-create-title" className="fm-panel__title">New meeting</h2>
              <p className="fm-panel__sub">
                {session
                  ? "Start an instant meeting or schedule for later."
                  : "Sign in to create meetings."}
              </p>
            </div>

            {!session ? (
              <Button variant="primary" size="lg" fullWidth onClick={() => navigate("/v2/login")}>
                Sign in to continue
              </Button>
            ) : (
              <form
                className="fm-form"
                onSubmit={(e) => { e.preventDefault(); handleCreateRoom(); }}
                noValidate
              >
                <Input
                  label="Agenda" required
                  placeholder="What's the meeting about?"
                  value={meetingAgenda}
                  onChange={(e) => setMeetingAgenda(e.target.value)}
                />

                <Input
                  label="Email" required type="email"
                  placeholder="your@email.com"
                  value={hostEmail}
                  onChange={(e) => setHostEmail(e.target.value)}
                  autoComplete="email"
                />

                <Input
                  label="Display name" required
                  placeholder="How should attendees see you?"
                  value={hostUsername}
                  onChange={(e) => setHostUsername(e.target.value)}
                  autoComplete="name"
                />

                <button
                  type="button"
                  className={cn("fm-disclosure-btn", showAdvanced && "fm-disclosure-btn--open")}
                  onClick={() => setShowAdvanced((v) => !v)}
                  aria-expanded={showAdvanced}
                  aria-controls="home-advanced"
                >
                  <span>Advanced options</span>
                  <span className="fm-disclosure-btn__caret" aria-hidden="true">▾</span>
                </button>

                {showAdvanced && (
                  <div id="home-advanced" className="fm-disclosure-body">
                    {/* Meeting type segmented control */}
                    <div className="fm-field">
                      <div className="fm-field__label-row">
                        <span className="fm-field__label">Meeting type</span>
                      </div>
                      <div className="fm-segmented" role="radiogroup" aria-label="Meeting type">
                        <button
                          type="button"
                          role="radio"
                          aria-checked={meetingType === "public"}
                          className={cn("fm-segmented__btn", meetingType === "public" && "fm-segmented__btn--active")}
                          onClick={() => { setMeetingType("public"); setFdaId(""); }}
                        >
                          🌐 Public
                        </button>
                        <button
                          type="button"
                          role="radio"
                          aria-checked={meetingType === "private"}
                          className={cn(
                            "fm-segmented__btn",
                            meetingType === "private" && "fm-segmented__btn--active",
                            !canCreatePrivate && "fm-segmented__btn--locked",
                          )}
                          disabled={!canCreatePrivate}
                          title={!canCreatePrivate ? "Private meetings require a Basic or Pro plan" : undefined}
                          onClick={() => {
                            if (!canCreatePrivate) {
                              navigate("/v2/pricing");
                              return;
                            }
                            setMeetingType("private");
                          }}
                        >
                          🔒 Private
                          {!canCreatePrivate && <span className="fm-lock-pill">PAID</span>}
                        </button>
                      </div>
                      {!canCreatePrivate && (
                        <p className="fm-field__hint">
                          Private meetings require a Basic or Pro plan.{" "}
                          <button
                            type="button"
                            className="fm-linkish"
                            onClick={() => navigate("/v2/pricing")}
                          >
                            Upgrade →
                          </button>
                        </p>
                      )}
                    </div>

                    {meetingType === "private" && (
                      <Input
                        label="User ID" required
                        placeholder="Authorised User ID"
                        value={fdaId}
                        onChange={(e) => setFdaId(e.target.value)}
                        autoComplete="off"
                      />
                    )}

                    {/* Capacity */}
                    <div className="fm-field">
                      <div className="fm-field__label-row">
                        <label className="fm-field__label" htmlFor="home-capacity">Max participants</label>
                      </div>
                      <select
                        id="home-capacity"
                        className="fm-select"
                        value={selectedCap}
                        onChange={handleCapacityChange}
                      >
                        {visibleCapacityOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                      <p className="fm-field__hint">
                        Your {session?.currentPlan || "free"} plan allows up to <b>{planCap}</b> participants per meeting.{" "}
                        {planCap < 100 && (
                          <button
                            type="button"
                            className="fm-linkish"
                            onClick={() => navigate("/v2/pricing")}
                          >
                            Upgrade for more →
                          </button>
                        )}
                      </p>
                    </div>

                    {isCustom && (
                      <>
                        <Input
                          label="Custom count"
                          type="number"
                          placeholder={`e.g. ${planCap}`}
                          value={customCap}
                          min={1}
                          max={planCap}
                          onChange={(e) => setCustomCap(e.target.value)}
                        />
                        {customCap && parseInt(customCap, 10) > planCap && (
                          <div className="fm-form-error">
                            Custom count exceeds your plan limit ({planCap}). Reduce the number or upgrade your plan.
                          </div>
                        )}
                      </>
                    )}

                    {/* Schedule */}
                    <label className="fm-check">
                      <input
                        type="checkbox"
                        checked={isScheduled}
                        onChange={() => setIsScheduled((v) => !v)}
                      />
                      <span>Schedule for later</span>
                    </label>

                    {isScheduled && (
                      <div className="fm-form-row">
                        <Input
                          label="Start" type="datetime-local"
                          value={startTime}
                          onChange={(e) => setStartTime(e.target.value)}
                        />
                        <Input
                          label="End" type="datetime-local"
                          value={endTime}
                          onChange={(e) => setEndTime(e.target.value)}
                        />
                      </div>
                    )}
                  </div>
                )}

                {createError && (
                  <div className="fm-inline-error" role="alert">
                    <span aria-hidden="true">⚠️</span>
                    <span>{createError}</span>
                  </div>
                )}

                {meetingLink && (
                  <div className="fm-sharebox">
                    <span className="fm-sharebox__label">📅 Scheduled — share this link</span>
                    <div className="fm-sharebox__row">
                      <a
                        href={meetingLink}
                        className="fm-sharebox__url"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {meetingLink}
                      </a>
                      <Button
                        type="button"
                        size="sm"
                        variant={linkCopied ? "secondary" : "primary"}
                        onClick={handleCopyLink}
                      >
                        {linkCopied ? "Copied ✓" : "Copy"}
                      </Button>
                    </div>
                  </div>
                )}

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  loading={isCreating}
                >
                  {isCreating
                    ? "Creating…"
                    : isScheduled
                      ? "Schedule meeting"
                      : "Start meeting now"}
                </Button>
              </form>
            )}
          </section>

          <div className="fm-panels__or" aria-hidden="true"><span>or</span></div>

          {/* JOIN PANEL */}
          <section className="fm-panel" aria-labelledby="home-join-title">
            <div className="fm-panel__head">
              <span className="fm-panel__eyebrow">↗ Drop in</span>
              <h2 id="home-join-title" className="fm-panel__title">Join meeting</h2>
              <p className="fm-panel__sub">Enter a Room ID or paste an invite link.</p>
            </div>

            <form className="fm-form" onSubmit={handleJoinRoom} noValidate>
              <Input
                label="Room ID or link"
                placeholder="e.g. abc-123-xyz or full link"
                value={roomId}
                onChange={(e) => setRoomId(e.target.value)}
                autoComplete="off"
              />

              {joinError && (
                <div className="fm-inline-error" role="alert">
                  <span aria-hidden="true">⚠️</span>
                  <span>{joinError}</span>
                </div>
              )}

              <Button type="submit" variant="secondary" size="lg" fullWidth>
                Join meeting
              </Button>

              <p style={{ fontSize: "var(--text-xs)", color: "var(--text-faint)", textAlign: "center" }}>
                You'll enter your name on the next screen.
              </p>
            </form>
          </section>
        </div>
      </div>

      <IconSidebarV2 />
    </div>
  );
}

export default HomePageV2;